import React from "react";
import { Box, Chip, FormControlLabel, Stack, Switch, TextField } from "@mui/material";

export type HealthFilter = "all" | "needs-attention" | "monitor" | "on-track" | "no-data";
export type StudyStatusFilter = "all" | "active" | "paused" | "draft" | "completed" | "archived";

interface StudyFiltersProps {
  healthFilter: HealthFilter;
  statusFilter: StudyStatusFilter;
  search: string;
  showTestStudies: boolean;
  showTableView: boolean;
  onHealthFilterChange: (f: HealthFilter) => void;
  onStatusFilterChange: (f: StudyStatusFilter) => void;
  onSearchChange: (s: string) => void;
  onShowTestStudiesChange: (v: boolean) => void;
  onShowTableViewChange: (v: boolean) => void;
}

const HEALTH_FILTERS: { value: HealthFilter; label: string; color?: string }[] = [
  { value: "all", label: "All Studies" },
  { value: "needs-attention", label: "Needs Attention", color: "error" },
  { value: "monitor", label: "Monitor", color: "warning" },
  { value: "on-track", label: "On Track", color: "success" },
];

const STATUS_FILTERS: { value: StudyStatusFilter; label: string }[] = [
  { value: "all", label: "Any Status" },
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
  { value: "completed", label: "Completed" },
  { value: "archived", label: "Archived" },
];

const StudyFilters: React.FC<StudyFiltersProps> = ({
  healthFilter,
  statusFilter,
  search,
  showTestStudies,
  showTableView,
  onHealthFilterChange,
  onStatusFilterChange,
  onSearchChange,
  onShowTestStudiesChange,
  onShowTableViewChange,
}) => {
  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        {/* Health filters */}
        {HEALTH_FILTERS.map((f) => (
          <Chip
            key={f.value}
            label={f.label}
            size="small"
            color={healthFilter === f.value ? (f.color as any) || "primary" : "default"}
            variant={healthFilter === f.value ? "filled" : "outlined"}
            onClick={() => onHealthFilterChange(f.value)}
            sx={{ fontWeight: healthFilter === f.value ? 600 : 400 }}
          />
        ))}

        <Box sx={{ width: 1, borderLeft: "1px solid", borderColor: "grey.300", height: 24, mx: 0.5 }} />

        {/* Status filters */}
        {STATUS_FILTERS.map((f) => (
          <Chip
            key={f.value}
            label={f.label}
            size="small"
            variant={statusFilter === f.value ? "filled" : "outlined"}
            onClick={() => onStatusFilterChange(f.value)}
            sx={{ fontWeight: statusFilter === f.value ? 600 : 400 }}
          />
        ))}

        <Box sx={{ flex: 1 }} />

        <TextField
          size="small"
          placeholder="Search studies..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={{ minWidth: 180, maxWidth: 260 }}
        />
      </Stack>

      <Stack direction="row" alignItems="center" spacing={2}>
        <FormControlLabel
          control={<Switch size="small" checked={showTestStudies} onChange={(e) => onShowTestStudiesChange(e.target.checked)} />}
          label="Show test studies"
          slotProps={{ typography: { variant: "caption", color: "text.secondary" } }}
        />
        <FormControlLabel
          control={<Switch size="small" checked={showTableView} onChange={(e) => onShowTableViewChange(e.target.checked)} />}
          label="Table view"
          slotProps={{ typography: { variant: "caption", color: "text.secondary" } }}
        />
      </Stack>
    </Box>
  );
};

export default StudyFilters;
